import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import SVG, { stopDragResize } from '../../../../../utils/svg';

import store from '../../../../../store';
import {
    setNewZone,
    setZoneDraw,
    updateZone,
} from '../../../../../store/actions/Store';

import { updateFloorPlanZone } from '../../../../../services/zones';

import { handleZonesResponse } from '../../containers/StoreMapContainer';



export const ZONES_LAYER_ID = 'ZonesLayer';

const zoneTextSize = 10;
const zoneTextOffset = 15;

const hasDifference = (a, b, numbers = []) => {
    let equal = true;
    Object.keys(a).forEach(k => {
        if (numbers.includes(k)) {
            if (Number(a[k]) !== Number(b[k])) {
                equal = false;
            }
        } else if (a[k] !== b[k]) {
            equal = false;
        }
    });
    return !equal;
};

const zoneFromRect = (node) => ({
    id: node.dataset.id ? Number(node.dataset.id) : undefined,
    position_x: Math.round(Number(node.getAttribute('x'))),
    position_y: Math.round(Number(node.getAttribute('y'))),
    width: Math.round(Number(node.getAttribute('width'))),
    depth: Math.round(Number(node.getAttribute('height'))),
});

const ZonesLayer = ({ scale = 1 }) => {
    const zones = useSelector((state) => state.store.zones) || [];
    const zonesVisibility = useSelector((state) => state.store.zonesVisibility) || {};

    const currentTab = useSelector((state) => state.store.currentTab);

    const isGridEnabled = useSelector((state) => state.store.isGridEnabled);
    const isDragEnabled = useSelector((state) => state.store.isDragEnabled);
    const isZoneDrawEnabled = useSelector((state) => state.store.isZoneDrawEnabled);

    const storeData = useSelector((state) => state.store.storeData);

    const [selectedZone, setSelectedZone] = useState(null);

    const zonesRef = useRef();

    const st = storeData ? storeData.floor_plan : null;
    const stw = st ? st.width || 1 : 1; // store width
    const std = st ? st.depth || 1 : 1; // store depth

    const gw = st ? st.grid_width || 100 : 100; // grid width
    const gd = st ? st.grid_depth || 100 : 100; // grid depth

    const snap =  Math.max(Math.min(gw, gd), 1);

    const up = currentTab==='zones';

    const handleMouseUp = (event) => {
        const zone = zoneFromRect(event.target);
        const state = store.getState();
        const oldZone = state.store.zones.find(z => z.id === zone.id);
        if (hasDifference(zone, oldZone, ['position_x', 'position_y', 'width', 'depth'])) {
            const newZone = { ...oldZone, ...zone };
            store.dispatch(updateZone(newZone));
            // TODO: save on button
            updateFloorPlanZone(newZone).then(handleZonesResponse);
        }
    };

    const handleMouseMove = (event, element) => {
        const state = store.getState();
        const zone = zoneFromRect(event.target);
        const oldZone = state.store.zones.find(z => z.id === zone.id);
        const newZone = { ...oldZone, ...zone };

        store.dispatch(updateZone(newZone));
    };

    const handleBackgroundClick = (event) => {
        event.stopPropagation();
        if (event.target !== zonesRef.current) return;
        if (selectedZone === null) return;
        const zoneRect = document.getElementById(selectedZone);
        stopDragResize(zoneRect);
        setSelectedZone(null);
    };

    const handleClick = (event) => {
        event.stopPropagation();

        const rectNode = event.target;

        if (selectedZone !== rectNode.id) {
            if (selectedZone !== null) {
                const oldZoneRect = document.getElementById(selectedZone);
                stopDragResize(oldZoneRect);
            }

            if (!isDragEnabled) return;

            setSelectedZone(rectNode.id);

            const zw = Math.round(Number(rectNode.getAttribute('width')));
            const zd = Math.round(Number(rectNode.getAttribute('height')));

            const rect = SVG.adopt(rectNode);

            rect.selectize({ rotationPoint: false });
            
            rect.resize({
                snapToGrid: isGridEnabled ? snap : 1,
                resizeLimits: { width: Math.max(gw, 1), height: Math.max(gd, 1) },
                constraint: { minX: 0, minY: 0, maxX: stw, maxY: std },
            }).on('resizedone', handleMouseUp);
            
            rect.draggable(
                (x, y) => {
                    let nx = x < 0 ? 0 : x;
                    let ny = y < 0 ? 0 : y;
                    nx = x > stw - zw ? stw - zw : nx;
                    ny = y > std - zd ? std - zd : ny;
                    if (!isGridEnabled || gw === 0 || gd === 0) {
                        return { x: nx, y: ny };
                    } else {
                        return { x: nx - nx % gw, y: ny - ny % gd }
                    }
                }
            ).on('dragend', handleMouseUp);

            rect.on('dragmove.namespace', handleMouseMove);
        }
    };

    const handleZoneDraw = () => {
        const rect = new SVG(ZONES_LAYER_ID).rect()
            .fill('rgba(0,0,0,0.2)').stroke('rgba(0,0,0,0.6)');

        rect.draw({ snapToGrid: isGridEnabled ? snap : 1 });

        rect.on('drawstop', function() {
            const { node } = this;

            const zone = zoneFromRect(node);
            
            rect.remove();

            store.dispatch(setNewZone(zone));
            store.dispatch(setZoneDraw(false));
        });
    };

    useEffect(() => {
        if (isZoneDrawEnabled) handleZoneDraw();
    }, [isZoneDrawEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (selectedZone !== null) {
            const zoneRect = document.getElementById(selectedZone);
            stopDragResize(zoneRect);
        }
    }, [isDragEnabled, isGridEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!up && selectedZone !== null) {
            const zoneRect = document.getElementById(selectedZone);
            stopDragResize(zoneRect);
            setSelectedZone(null);
        }
    }, [up]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <svg
            width={stw}
            height={std}
            ref={zonesRef}
            id={ZONES_LAYER_ID}
            onClick={handleBackgroundClick}
            style={{
                position: 'absolute',
                zIndex: up ? 1 : 'auto',
                outline: isZoneDrawEnabled ? '1px solid red' : '0 none',
            }}
        >
            {zones.map(zone => {
                const id = `Zone_${zone.id}`;
                return (
                    <g key={zone.id}>
                        <rect
                            id={id}
                            data-id={zone.id}
                            fill={zone.color}
                            fillOpacity={0.25}
                            stroke={zone.color}
                            strokeOpacity={0.75}
                            strokeWidth={1 / scale}
                            x={zone.position_x}
                            y={zone.position_y}
                            width={zone.width}
                            height={zone.depth}
                            onClick={handleClick}
                            opacity={zonesVisibility[zone.id] ? 1 : 0.25}
                            style={{ cursor: selectedZone === id ? 'move': 'pointer' }}
                        />
                        {selectedZone === id && (
                            <text
                                x={Number(zone.position_x) + zoneTextOffset}
                                y={Number(zone.position_y) + zoneTextOffset}
                                fontSize={`${zoneTextSize/scale}px`}
                                textAnchor={'start'}
                                opacity={0.5}
                                style={{ textShadow: '0 0 0.2rem white' }}
                            >
                                <tspan dy="1em">{`x: ${zone.position_x / 100}`}</tspan>
                                <tspan x={Number(zone.position_x) + zoneTextOffset} dy="1.5em">
                                    {`y: ${zone.position_y / 100}`}
                                </tspan>
                            </text>
                        )}
                    </g>
                )}
            )}
        </svg>
    );
};

export default ZonesLayer;
